import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Button, Tooltip, notification } from "antd";
import { CheckOutlined, CloseOutlined, SignatureOutlined } from "@ant-design/icons";
import { enhanceText } from "../../services/aiAPI";
import { getAiEnhanceLabels } from "../../constants/aiEnhanceUi";

/**
 * Reusable AI text enhancement: star trigger, inline accept/dismiss popover, API + abort.
 * Parent supplies how to read text/selection and how to apply the result (any editor).
 *
 * @param {object} props
 * @param {'question' | 'response'} props.target - Labels for tooltips / empty-selection warning
 * @param {() => { text: string, rangeClone: Range | null, hadRange: boolean } | null} props.getEnhanceContext
 *        Return null if the editor surface is not available. `hadRange` should be true only when
 *        `rangeClone` is valid and the selection was inside the editor.
 * @param {(enhanced: string, meta: { rangeClone: Range | null, hadRange: boolean }) => void} props.onApplyEnhanced
 * @param {string} [props.wrapperClassName] - Added to the outer wrapper (e.g. toolbar alignment)
 */
const AiTextEnhanceControl = ({
  target = "question",
  getEnhanceContext,
  onApplyEnhanced,
  wrapperClassName = "relative ml-auto flex shrink-0 flex-col items-end self-center",
}) => {
  const labels = getAiEnhanceLabels(target);
  const abortRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(null);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const dismiss = () => setPending(null);

  const accept = () => {
    if (!pending) return;
    const { enhanced, rangeClone, hadRange } = pending;
    onApplyEnhanced(enhanced, { rangeClone, hadRange });
    setPending(null);
    notification.success({
      message: "Suggestion applied",
      description: "The text was updated.",
    });
  };

  const run = async () => {
    setPending(null);
    const ctx = getEnhanceContext?.();
    if (!ctx) return;
    const { text, rangeClone, hadRange } = ctx;
    if (!text || text.length < 2) {
      notification.warning({
        message: labels.selectTitle,
        description: labels.selectDescription,
      });
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    try {
      const { enhanced_text: enhanced } = await enhanceText(text, {
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      if (!enhanced) {
        throw new Error("Empty response");
      }
      setPending({
        enhanced,
        rangeClone,
        hadRange,
      });
    } catch (err) {
      if (axios.isCancel(err) || err?.code === "ERR_CANCELED") return;
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
            ? detail.map((d) => d?.msg || d).join(" ")
            : err?.message || "Request failed";
      if (status === 402) {
        notification.warning({ message: "Upgrade to continue", description: msg });
      } else {
        notification.error({ message: "Enhance failed", description: msg });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={wrapperClassName}>
      <Tooltip title={labels.title}>
        <Button
          type="default"
          icon={<SignatureOutlined />}
          children="AI Enhance"
          onClick={(e) => {
            e.preventDefault();
            run();
          }}
          size="small"
          loading={loading}
          className="text-gray-600 hover:text-gray-900"
          aria-label={labels.title}
        />
      </Tooltip>
      {pending && (
        <div
          className="absolute top-full right-0 mt-1 z-[100] w-80 rounded-md border border-gray-200 bg-white shadow-lg p-3 text-left"
          role="status"
        >
          <p className="text-xs text-gray-600 mb-2 m-0">Accept this suggestion?</p>
          <div
            className="text-sm max-h-28 overflow-y-auto mb-3 rounded border border-gray-100 bg-gray-50 p-2 break-words [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:list-inside"
            dangerouslySetInnerHTML={{ __html: pending.enhanced }}
          />
          <div className="flex justify-end gap-1">
            <Tooltip title="Dismiss">
              <Button
                type="text"
                danger
                size="small"
                icon={<CloseOutlined />}
                onClick={(e) => {
                  e.preventDefault();
                  dismiss();
                }}
                aria-label="Dismiss"
              />
            </Tooltip>
            <Tooltip title="Accept">
              <Button
                type="primary"
                size="small"
                icon={<CheckOutlined />}
                onClick={(e) => {
                  e.preventDefault();
                  accept();
                }}
                aria-label="Accept"
              />
            </Tooltip>
          </div>
        </div>
      )}
    </div>
  );
};

export default AiTextEnhanceControl;
